import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import CTAPanel from "@/components/base/CTAPanel";
import NAICSCodesSection from "@/components/base/NAICSCodesSection";
import RelatedContent from "@/components/feature/RelatedContent";

const whatWeDo = [
  {
    title: "Enterprise Infrastructure and Network Engineering",
    description: "LAN/WAN engineering, server and collaboration support, virtualization, and infrastructure modernization for enterprise environments.",
    icon: "ri-server-line",
  },
  {
    title: "Database and Data Platform Services",
    description: "Database engineering, administration, and platform support for Oracle, SQL Server, and relational database environments.",
    icon: "ri-database-2-line",
  },
  {
    title: "Application and Platform Engineering",
    description: "Enterprise application support, platform engineering, software maintenance, and data development.",
    icon: "ri-layout-grid-line",
  },
  {
    title: "Technical Staffing and Delivery Teams",
    description: "Qualified engineers, administrators, developers, analysts, and subject-matter experts for defined technical workstreams.",
    icon: "ri-team-line",
  },
  {
    title: "Microsoft Power Platform and SharePoint",
    description: "Power Apps, Power Automate, Power BI, SharePoint workflows, and user enablement for enterprise business applications.",
    icon: "ri-window-line",
  },
  {
    title: "Application Integration and Data Development",
    description: "Integration support connecting applications and data sources, plus data development for enterprise programs.",
    icon: "ri-node-tree",
  },
];

const approachValues = [
  {
    title: "Evidence-Based",
    description: "We communicate capabilities and past performance using documented, supportable information.",
    icon: "ri-focus-3-line",
  },
  {
    title: "Partnership-Oriented",
    description: "We work within prime-contractor, subcontractor, and teaming structures with clearly defined responsibilities.",
    icon: "ri-contract-line",
  },
  {
    title: "Technically Focused",
    description: "We align experienced professionals and practical technology expertise with each engagement's requirements.",
    icon: "ri-cpu-line",
  },
  {
    title: "Delivery-Minded",
    description: "We emphasize clear scope, communication, documentation, quality, and operational continuity.",
    icon: "ri-check-double-line",
  },
];

const markets = [
  "Federal government",
  "State and local government",
  "Transportation",
  "Healthcare",
  "Higher education",
  "Utilities and infrastructure",
  "Commercial enterprises",
];

const companyInfo = [
  { label: "Legal Name", value: "Mitaja Corporation" },
  { label: "Business Status", value: "Small, for-profit corporation" },
  { label: "UEI", value: "C9PPAQCM5DD9" },
  { label: "CAGE Code", value: "7PBY4" },
  { label: "Location", value: "Fulton, Maryland" },
  { label: "Email", value: "governmentsales@mitajacorp.com" },
  { label: "Website", value: "mitajacorp.com" },
];

export default function About() {
  return (
    <Layout>
      <PageHero
        title="Technology Expertise. Mission-Focused Delivery."
        subtitle="About Mitaja Corporation"
        description="Mitaja Corporation provides enterprise IT engineering, database and platform services, application support, and specialist technical delivery teams for public-sector and commercial environments. Through subcontracting and teaming relationships, Mitaja helps organizations access experienced engineers, administrators, developers, analysts, and subject-matter experts."
        size="lg"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            <div>
              <SectionHeader
                title="Who We Are"
                subtitle="About Us"
                description="Mitaja Corporation is a small, for-profit technology company based in Fulton, Maryland. We combine technical expertise with a flexible delivery model designed to support complex programs, defined technical workstreams, and evolving workforce requirements."
                centered={false}
              />
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                We provide enterprise IT engineering, database and platform services, application support, and specialist technical delivery teams for public-sector and commercial environments.
              </p>
              <p className="text-base text-foreground-600 leading-relaxed">
                Through subcontracting and teaming relationships, we help organizations access experienced engineers, administrators, developers, analysts, and subject-matter experts for mission-focused delivery.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20enterprise%20technology%20team%20collaborating%20around%20servers%20and%20network%20infrastructure%20in%20a%20clean%20professional%20data%20center%2C%20warm%20neutral%20tones%2C%20no%20readable%20faces%2C%20editorial%20photography%2C%20soft%20natural%20lighting%2C%20high%20detail&width=800&height=600&seq=about-who-we-are&orientation=landscape"
                  alt="Enterprise technology team working within a modern data center environment"
                  className="w-full h-80 md:h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-background-100">
            <div className="container-page">
              <SectionHeader
                title="Vision & Mission"
                subtitle="Our Purpose"
                description="The principles that guide Mitaja's work for public-sector and commercial clients."
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="card-base p-8 border-t-4 border-t-accent-500">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                    <i className="ri-eye-line text-primary-600"></i>
                  </div>
                  <h4 className="text-lg font-bold text-foreground-900 mb-3">
                    Our Vision
                  </h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    To be a trusted technology partner that helps public-sector and commercial organizations build reliable, mission-focused delivery teams and achieve their technology objectives through documented expertise and dependable execution.
                  </p>
                </div>
                <div className="card-base p-8 border-t-4 border-t-accent-500">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                    <i className="ri-compass-3-line text-primary-600"></i>
                  </div>
                  <h4 className="text-lg font-bold text-foreground-900 mb-3">
                    Our Mission
                  </h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    To deliver enterprise IT engineering, database and platform services, application support, and specialist technical talent through subcontracting and teaming relationships — combining technical expertise with clear communication, quality, and operational continuity.
                  </p>
                </div>
              </div>
            </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-page">
          <SectionHeader
            title="What We Do"
            subtitle="Capabilities"
            description="The technology capabilities Mitaja delivers across public-sector and commercial engagements."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatWeDo.map((cap) => (
              <div key={cap.title} className="card-base p-6">
                <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center mb-5">
                  <i className={`${cap.icon} text-primary-600 text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-2">
                  {cap.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {cap.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-background-100">
        <div className="container-page">
          <SectionHeader
            title="Our Experience"
            subtitle="Proven Delivery"
            description="Documented experience delivered through established partnerships and teaming structures."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="card-base p-8">
              <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center mb-4">
                <i className="ri-building-line text-accent-600"></i>
              </div>
              <h4 className="text-lg font-bold text-foreground-900 mb-3">
                Federal Enterprise IT Support
              </h4>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Mitaja has documented experience supporting federal enterprise IT environments through higher-tier contracting partners. This experience includes LAN/WAN engineering, server and collaboration support, virtualization, Oracle and SQL database administration, enterprise software support, application integration, and specialist technical staffing.
              </p>
            </div>
            <div className="card-base p-8">
              <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center mb-4">
                <i className="ri-global-line text-accent-600"></i>
              </div>
              <h4 className="text-lg font-bold text-foreground-900 mb-3">
                International & Public-Sector Consulting
              </h4>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Mitaja has also provided partner-supported consulting across public-sector and institutional technology initiatives in India. These engagements have involved requirements analysis, workflow design, data and integration planning, web and mobile solutions, GIS-enabled operations, user acceptance testing, and delivery governance.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-page">
          <SectionHeader
            title="Our Approach"
            subtitle="How We Deliver"
            description="Four principles guide how Mitaja supports every engagement."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approachValues.map((value) => (
              <div key={value.title} className="card-base p-6 border-t-4 border-t-accent-500">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <i className={`${value.icon} text-primary-600`}></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-background-100">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <SectionHeader
                title="Markets We Support"
                subtitle="Sectors"
                description="The public-sector and commercial markets where Mitaja applies its technology capabilities."
                centered={false}
              />
              <ul className="space-y-3">
                {markets.map((market) => (
                  <li key={market} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                    <span className="text-base text-foreground-700 font-medium">
                      {market}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20aerial%20view%20of%20connected%20city%20infrastructure%20with%20glowing%20network%20lines%20representing%20transportation%2C%20healthcare%2C%20education%20and%20utilities%20sectors%2C%20deep%20navy%20and%20warm%20amber%20tones%2C%20modern%20professional%20digital%20art%2C%20no%20text%2C%20high%20detail&width=800&height=600&seq=about-markets&orientation=landscape"
                  alt="Abstract visualization of interconnected public-sector and commercial markets supported by Mitaja"
                  className="w-full h-80 md:h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-page">
          <SectionHeader
            title="Company Information"
            subtitle="Registered Business Details"
            description="Key business and registration information for Mitaja Corporation."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {companyInfo.map((item) => (
              <div key={item.label} className="card-base p-6 bg-background-100 border border-background-200/80">
                <p className="text-xs font-semibold text-secondary-700 uppercase tracking-wider mb-2">
                  {item.label}
                </p>
                <p className="text-base font-bold text-foreground-900">
                  {item.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <NAICSCodesSection />
          </div>
        </div>
      </section>

      <RelatedContent
        subtitle="Company Information"
        title="Government Contracting & Delivery Details"
        description="Review Mitaja's verified contracting credentials, capability statement, and documented technology delivery experience."
        links={[
          {
            label: "Government Contracting Information",
            description: "UEI, CAGE, NAICS codes, GSA MAS, state certifications, and procurement details for government buyers.",
            href: "/resources/contracting-information",
            icon: "ri-file-shield-2-line",
          },
          {
            label: "Capability Statement",
            description: "Mitaja's capability statement covering company profile, core capabilities, and documented experience.",
            href: "/resources/capability-statement",
            icon: "ri-file-list-line",
          },
          {
            label: "Government & Enterprise Experience",
            description: "Explore documented technology-delivery experience across federal, state and local, and commercial programs.",
            href: "/experience",
            icon: "ri-line-chart-line",
          },
        ]}
      />

      <CTAPanel
        title="Build Your Next Delivery Team with Mitaja"
        description="Connect with Mitaja to discuss subcontracting, teaming, technical staffing, or technology delivery requirements."
        primaryAction={{ label: "Contact Mitaja", href: "/contact" }}
        variant="dark"
      />
    </Layout>
  );
}